import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { SealCheck, MapPin, Package, Clock, Leaf, Users, Globe, Shield, LinkSimple, ArrowLeft, Download, CurrencyEth, Share, Spinner, Truck, ClipboardText } from '@phosphor-icons/react';
import Navbar from '@/components/custom/Navbar';
import Footer from '@/components/custom/Footer';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PassportPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nftData, setNftData] = useState(null);
  const [mintingNft, setMintingNft] = useState(false);
  const [supplyChain, setSupplyChain] = useState(null);
  const [requestingAudit, setRequestingAudit] = useState(false);

  const irisId = localStorage.getItem('ginsgood_iris_id');

  useEffect(() => {
    const fetchPassport = async () => {
      try {
        const response = await axios.get(`${API}/passport/${id}`);
        setPassport(response.data);
        
        // Also check NFT status
        try {
          const nftResponse = await axios.get(`${API}/nft/${id}`);
          setNftData(nftResponse.data);
        } catch (nftError) {
          // NFT not minted yet - this is expected for new certificates
          console.debug('NFT not yet minted for certificate:', id);
        }

        // Get supply chain info
        try {
          const scResponse = await axios.get(`${API}/supply-chain/${id}`);
          setSupplyChain(scResponse.data);
        } catch (scError) {
          // No supply chain yet - this is expected
          console.debug('No supply chain data for certificate:', id);
        }
      } catch (error) {
        console.error('Error fetching passport:', error);
        toast.error('Failed to load certificate');
      } finally {
        setLoading(false);
      }
    };
    fetchPassport();
  }, [id]);

  const handleDownloadPdf = async () => {
    try {
      const response = await axios.get(`${API}/certificate/${id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `GinsGood_Certificate_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Certificate downloaded!');
    } catch (error) {
      toast.error('Failed to download certificate');
    }
  };

  const handleMintNft = async () => {
    setMintingNft(true);
    try {
      const response = await axios.post(`${API}/nft/mint/${id}`);
      if (response.data.success) {
        setNftData(response.data);
        toast.success('NFT minted successfully!');
      }
    } catch (error) {
      toast.error('Failed to mint NFT');
    } finally {
      setMintingNft(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: passport?.product?.name || 'GinsGood Certificate',
          text: `Check out this verified sustainable product certificate!`,
          url: window.location.href
        });
      } catch (shareError) {
        // User cancelled share dialog - this is expected behavior
        if (shareError.name !== 'AbortError') {
          console.error('Share failed:', shareError);
        }
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 max-w-3xl mx-auto px-6">
          <div className="animate-pulse space-y-4">
            <div className="h-48 bg-muted rounded-xl" />
            <div className="h-32 bg-muted rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!passport) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 max-w-3xl mx-auto px-6 text-center py-24">
          <h1 className="text-2xl font-medium mb-4">Passport not found</h1>
          <Link to="/scan">
            <Button variant="outline">Search for Product</Button>
          </Link>
        </div>
      </div>
    );
  }

  const breakdown = passport.score_breakdown || {};
  const categoryPercentages = breakdown.category_percentages || {};

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'authenticity': return <Shield weight="duotone" className="w-5 h-5" />;
      case 'heritage': return <Globe weight="duotone" className="w-5 h-5" />;
      case 'social_impact': return <Users weight="duotone" className="w-5 h-5" />;
      case 'sustainability': return <Leaf weight="duotone" className="w-5 h-5" />;
      default: return null;
    }
  };

  const getCategoryColor = (category) => {
    switch(category) {
      case 'authenticity': return '#2C3E2D';
      case 'heritage': return '#C26D53';
      case 'social_impact': return '#D4A373';
      case 'sustainability': return '#4A6C4C';
      default: return '#6B6659';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-20 pb-16">
        <div className="max-w-3xl mx-auto px-6">
          {/* Back Link */}
          <div className="py-4">
            <Link to="/scan" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Scanner
            </Link>
          </div>

          {/* Certificate Header */}
          <Card className="overflow-hidden mb-6" data-testid="passport-card">
            <div className="bg-[#2C3E2D] text-white p-8">
              <div className="flex items-start justify-between">
                <div>
                  <Badge variant="secondary" className="mb-4 bg-white/20 border-white/30">
                    <SealCheck weight="duotone" className="w-4 h-4 mr-2" />
                    Verified Certificate
                  </Badge>
                  <h1 className="text-2xl md:text-3xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {passport.product?.name}
                  </h1>
                  <p className="text-white/70">{passport.product?.category}</p>
                </div>
                {passport.qr_code_base64 && (
                  <img 
                    src={`data:image/png;base64,${passport.qr_code_base64}`}
                    alt="QR Code"
                    className="w-24 h-24 rounded-lg bg-white p-1"
                  />
                )}
              </div>
              
              <div className="mt-6 pt-6 border-t border-white/20">
                <p className="text-sm text-white/60 mb-1">Certificate Number</p>
                <p className="font-mono text-lg">{passport.certificate_number}</p>
              </div>
            </div>

            {/* GinsGood Score */}
            <div className="p-8 bg-secondary/30">
              <div className="text-center mb-8">
                <p className="text-sm text-muted-foreground mb-2">GinsGood Sustainability Score</p>
                <div className="relative w-40 h-40 mx-auto">
                  <svg className="w-40 h-40 -rotate-90">
                    <circle
                      cx="80"
                      cy="80"
                      r="70"
                      fill="none"
                      stroke="hsl(40 18% 89%)"
                      strokeWidth="12"
                    />
                    <circle
                      cx="80"
                      cy="80"
                      r="70"
                      fill="none"
                      stroke="#4A6C4C"
                      strokeWidth="12"
                      strokeDasharray={`${(passport.ginsgood_score / 100) * 439.8} 439.8`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-4xl font-bold text-[#4A6C4C]">{passport.ginsgood_score}</span>
                    <span className="text-sm text-muted-foreground">out of 90</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">* Maximum achievable score is 90%</p>
              </div>

              {/* Category Breakdown */}
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(categoryPercentages).map(([category, score]) => (
                  <div key={category} className="bg-white rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${getCategoryColor(category)}15`, color: getCategoryColor(category) }}
                      >
                        {getCategoryIcon(category)}
                      </div>
                      <span className="text-sm font-medium capitalize">{category.replace('_', ' ')}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Progress value={score} className="flex-1 h-2" />
                      <span className="text-sm font-semibold" style={{ color: getCategoryColor(category) }}>
                        {score}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Product & Artisan Details */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <Card className="p-6">
              <h3 className="font-medium mb-4 flex items-center gap-2">
                <Package weight="duotone" className="w-5 h-5 text-primary" />
                Product Details
              </h3>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-muted-foreground">Description</dt>
                  <dd className="mt-1">{passport.product?.description}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Material</dt>
                  <dd className="font-medium">{passport.material?.type}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Origin</dt>
                  <dd className="font-medium">{passport.material?.origin}</dd>
                </div>
              </dl>
            </Card>

            <Card className="p-6">
              <h3 className="font-medium mb-4 flex items-center gap-2">
                <Users weight="duotone" className="w-5 h-5 text-primary" />
                Artisan Details
              </h3>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Name</dt>
                  <dd className="font-medium">{passport.artisan?.name}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Location</dt>
                  <dd className="flex items-center gap-1">
                    <MapPin weight="duotone" className="w-4 h-4" />
                    {passport.artisan?.location}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Technique</dt>
                  <dd className="font-medium">{passport.artisan?.craftsmanship}</dd>
                </div>
              </dl>
            </Card>
          </div>

          {/* Blockchain Verification */}
          <Card className="p-6 mb-6">
            <h3 className="font-medium mb-4 flex items-center gap-2">
              <LinkSimple weight="duotone" className="w-5 h-5 text-primary" />
              Blockchain Verification
            </h3>
            <div className="bg-secondary/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Blockchain Hash</span>
                <Badge variant="secondary" className="bg-[#4A6C4C] text-white">
                  Verified
                </Badge>
              </div>
              <code className="text-xs font-mono break-all block">
                {passport.verification?.blockchain_hash}
              </code>
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Seller Verified</span>
                  <p className="font-medium">{passport.verification?.seller_verified ? 'Yes (Iris Authenticated)' : 'No'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Issued</span>
                  <p className="font-medium">{new Date(passport.verification?.issued_at).toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          </Card>

          {/* NFT Status */}
          {nftData?.minted && (
            <Card className="p-6 mb-6 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
              <h3 className="font-medium mb-4 flex items-center gap-2">
                <CurrencyEth weight="duotone" className="w-5 h-5 text-purple-600" />
                NFT Certificate
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Mint Address</span>
                  <code className="font-mono text-xs">{nftData.nft_mint_address?.slice(0, 16)}...</code>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Network</span>
                  <Badge variant="secondary" className="bg-purple-100 text-purple-800">{nftData.network}</Badge>
                </div>
                <a 
                  href={nftData.explorer_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:underline text-xs"
                >
                  View on Solana Explorer →
                </a>
              </div>
            </Card>
          )}

          {/* Driver Details */}
          {breakdown.driver_details && Object.keys(breakdown.driver_details).length > 0 && (
            <Card className="p-6">
              <h3 className="font-medium mb-4">Score Drivers</h3>
              <div className="space-y-4">
                {Object.entries(breakdown.driver_details).map(([driverId, detail]) => (
                  <div key={driverId} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{detail.question}</p>
                      <p className="text-xs text-muted-foreground">{detail.answer}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-semibold" style={{ color: getCategoryColor(detail.category) }}>
                        {detail.score}/{detail.max_score}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Supply Chain Preview */}
          <Card className="p-6 bg-gradient-to-br from-green-50 to-teal-50 border-green-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Truck weight="duotone" className="w-5 h-5 text-green-600" />
                <h3 className="font-medium">Supply Chain Traceability</h3>
              </div>
              {supplyChain?.is_complete ? (
                <Badge className="bg-green-100 text-green-800">Complete</Badge>
              ) : (
                <Badge variant="secondary">{supplyChain?.total_stages || 0}/5 Stages</Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              {supplyChain?.total_stages > 0 
                ? `Track the journey from raw material to finished product with ${supplyChain.total_stages} verified stages.`
                : 'No supply chain data recorded yet.'}
            </p>
            <Button 
              variant="outline" 
              className="w-full gap-2"
              onClick={() => navigate(`/supply-chain/${id}`)}
              data-testid="view-supply-chain-btn"
            >
              <Truck className="w-4 h-4" />
              View Supply Chain
            </Button>
          </Card>

          {/* Request Audit */}
          {!passport.auditor_verified && (
            <Card className="p-6 border-dashed border-2">
              <div className="flex items-center gap-2 mb-4">
                <ClipboardText weight="duotone" className="w-5 h-5 text-primary" />
                <h3 className="font-medium">Third-Party Verification</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Enhance trust by getting this certificate verified by an independent auditor from our network.
              </p>
              <Button 
                variant="outline" 
                className="w-full gap-2"
                onClick={() => navigate('/auditors')}
                data-testid="request-audit-btn"
              >
                <ClipboardText className="w-4 h-4" />
                Request Audit
              </Button>
            </Card>
          )}

          {passport.auditor_verified && (
            <Card className="p-6 bg-green-50 border-green-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <SealCheck weight="fill" className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-medium text-green-800">Auditor Verified</h3>
                  <p className="text-sm text-green-600">This certificate has been verified by a third-party auditor</p>
                </div>
              </div>
            </Card>
          )}

          {/* Actions */}
          <div className="mt-8 space-y-3">
            <div className="flex gap-3">
              <Button onClick={handleDownloadPdf} variant="outline" className="flex-1 gap-2" data-testid="download-pdf-btn">
                <Download weight="bold" className="w-5 h-5" />
                Download PDF
              </Button>
              <Button onClick={handleShare} className="flex-1 gap-2">
                <Share weight="bold" className="w-5 h-5" />
                Share
              </Button>
            </div>
            
            {!nftData?.minted && (
              <Button 
                onClick={handleMintNft} 
                disabled={mintingNft}
                className="w-full gap-2 bg-purple-600 hover:bg-purple-700"
                data-testid="mint-nft-btn"
              >
                {mintingNft ? (
                  <>
                    <Spinner className="w-5 h-5 animate-spin" />
                    Minting NFT...
                  </>
                ) : (
                  <>
                    <CurrencyEth weight="bold" className="w-5 h-5" />
                    Mint as NFT on Solana
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PassportPage;
